import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_CLICK_ADDRESS_PAGE_SHARE_ICON, GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON } from 'constants/events';
import { useFirstVisit } from 'hooks/use-first-visit';
import { Namespaces, LOCALE_COMMON_PATHS } from 'types/locales';

export interface ListingStickyFooterInput {
  /** Callback for first-time users (Request More Info) */
  onRequestInfo: () => void;
  /** Callback for return users (Book a Showing) */
  onBookShowing: () => void;
  /** When the listing is coming soon, the CTA collapses to a single "Contact Agent" info request (no tour/booking). */
  isComingSoon?: boolean;
  /** Share configuration */
  share?: {
    text: string;
    url: string;
    onShare?: () => void;
  };
  /** Favorite configuration */
  favorite?: {
    isFavorited: boolean;
    onToggle: () => void;
  };
}

export interface ListingStickyFooterData {
  primaryAction: {
    label: string;
    onClick: () => void;
    testId: string;
  };
  shareLabel: string;
  favoriteLabel: string;
  handleShare: () => void;
  handleFavorite: () => void;
}

/**
 * useListingStickyFooter - Hook for listing sticky footer business logic
 * 
 * Handles:
 * - First-time vs return user detection
 * - i18n labels
 * - Share handler (native share or clipboard fallback)
 */
export function useListingStickyFooter({
  onRequestInfo,
  onBookShowing,
  isComingSoon = false,
  share,
  favorite,
}: ListingStickyFooterInput): ListingStickyFooterData {
  const { t } = useTranslation(Namespaces.COMMON);
  const isFirstVisit = useFirstVisit();

  // Coming soon listings can't be toured or booked yet, so both visitor states
  // collapse to a single "Contact Agent" info request.
  const comingSoonAction = {
    label: t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_CONTACT_AGENT, { defaultValue: 'Contact Agent' }),
    onClick: onRequestInfo,
    testId: 'sticky-footer-contact-agent',
  };

  // Determine primary action based on user type
  const visitorAction = isFirstVisit
    ? {
        label: t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_REQUEST_INFO, { defaultValue: 'Request More Info' }),
        onClick: onRequestInfo,
        testId: 'sticky-footer-request-info',
      }
    : {
        label: t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_BOOK_SHOWING, { defaultValue: 'Book a Showing' }),
        onClick: onBookShowing,
        testId: 'sticky-footer-book-showing',
      };

  const primaryAction = isComingSoon ? comingSoonAction : visitorAction;

  // Share label
  const shareLabel = t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_SHARE, { defaultValue: 'Share listing' });

  // Favorite label (dynamic based on state)
  const favoriteLabel = favorite?.isFavorited
    ? t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_UNFAVORITE, { defaultValue: 'Remove from favorites' })
    : t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_FAVORITE, { defaultValue: 'Add to favorites' });

  // Share handler
  const handleShare = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_SHARE_ICON);

    if (share?.onShare) {
      share.onShare();
      return;
    }

    if (!share) return;

    // Native share or clipboard fallback
    if (typeof navigator.share === 'undefined') {
      navigator.clipboard.writeText(share.url).catch(error => {
        console?.error('Clipboard api not available: ' + error);
      });
    } else {
      navigator.share({
        title: document.title,
        url: share.url,
        text: share.text,
      }).catch(console.log);
    }
  }, [share]);

  // Favorite handler
  const handleFavorite = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON);
    favorite?.onToggle();
  }, [favorite]);

  return {
    primaryAction,
    shareLabel,
    favoriteLabel,
    handleShare,
    handleFavorite,
  };
}
